import { audioRecordingModule } from "@/modules/audio-recording";
import {
  AudioRecordingError,
  AudioRecordingErrorSource,
  AudioRecordingErrorStage,
} from "@/domains/errors";
import { logger } from "@/modules/logger";
import { getNetworkInfo } from "@/domains/network/status";
import { createDetachedBlobFromBufferView } from "@/modules/audio-recording/blob-utils";
import {
  MediaRecorderTranscriptFinalTurn,
  MediaRecorderTranscriptPartialTurn,
  MediaTranscriberEngine,
  MediaTranscriberEventHandlers,
  TranscriptionStatus,
} from "@/store/audio/recording/media-types";
import {
  AUDIO_RECORDER_TRANSCRIBER_SAMPLE_RATE_HZ,
  AUDIO_RECORDER_TRANSCRIBER_WS_URL,
} from "./transcriber.constants";
import audioProcessorWorklet from "@/modules/audio-recording/worklet/audio-processor.js?raw";
import {
  AssemblyAiTranscriberPayloadType,
  WebMediaTranscriberInitialConnectRetryConfig,
  WebMediaTranscriberOptions,
} from "@/store/audio/recording/media-transcriber/transcriber.types";
import { AssemblyAiToken } from "@/modules/audio-recording/audio-recording.types";
import { TranscriptionMonitor } from "@/store/audio/recording/media-transcriber/transcription-monitor";
import pRetry, { AbortError, FailedAttemptError } from "p-retry";

const DEFAULT_INITIAL_CONNECT_RETRY_CONFIG: Required<WebMediaTranscriberInitialConnectRetryConfig> =
  {
    retries: 5,
    factor: 2,
    minTimeoutMs: 500,
    maxTimeoutMs: 4_000,
  };

export class WebMediaTranscriber implements MediaTranscriberEngine {
  private readonly tokenProvider: () => Promise<AssemblyAiToken>;
  private readonly stream: MediaStream;
  private readonly sampleRateHz = AUDIO_RECORDER_TRANSCRIBER_SAMPLE_RATE_HZ;
  private readonly websocketUrl = AUDIO_RECORDER_TRANSCRIBER_WS_URL;

  // 12 * 8ms ≈ 96ms per flush
  // This is within the bounds of what assembly-ai expects
  private readonly targetChunkCount = 12;
  private readonly maxChunkCount = 125;
  private readonly flushIntervalMs = 60;
  private readonly tailFlushTimeoutMs = 250;
  private readonly initialConnectRetryConfig: Required<WebMediaTranscriberInitialConnectRetryConfig>;

  private websocket: WebSocket | null = null;
  private audioContext: AudioContext | null = null;
  private audioSource: MediaStreamAudioSourceNode | null = null;
  private audioWorkletNode: AudioWorkletNode | null = null;
  private processingQueue: Promise<void> = Promise.resolve();
  private handlers: MediaTranscriberEventHandlers = {};
  private workletModuleUrl: string | null = null;
  private bufferedChunks: ArrayBuffer[] = [];
  private flushIntervalHandle: ReturnType<typeof setInterval> | null = null;
  private tailFlushTimeoutHandle: ReturnType<typeof setTimeout> | null = null;
  private isPaused = false;
  private reconnectTimeoutHandle: ReturnType<typeof setTimeout> | null = null;
  private readonly transport = new TranscriptionMonitor({
    maxReconnectAttempts: 5,
  });
  private isReconnecting = false;
  private isStarting = false;
  private shouldAttemptReconnect = true;

  private readonly customVocabulary: string[] | undefined;

  constructor({
    tokenProvider,
    stream,
    customVocabulary,
    initialConnectRetryConfig,
  }: WebMediaTranscriberOptions) {
    this.tokenProvider = tokenProvider;
    this.stream = stream;
    this.customVocabulary = customVocabulary;
    this.initialConnectRetryConfig = {
      ...DEFAULT_INITIAL_CONNECT_RETRY_CONFIG,
      ...initialConnectRetryConfig,
    };
  }

  setEventHandlers(handlers: MediaTranscriberEventHandlers): void {
    this.handlers = handlers ?? {};
  }

  private emitError(
    error: unknown,
    stage?: AudioRecordingErrorStage,
    details?: Record<string, string | number | boolean | null | undefined>
  ): void {
    if (
      error instanceof AudioRecordingError &&
      error.source === AudioRecordingErrorSource.AssemblyAI
    ) {
      this.handlers.onError?.({ error });
      return;
    }
    let message = error instanceof Error ? error.message : String(error);
    if (
      (!message || message === "[object Event]") &&
      typeof Event !== "undefined" &&
      error instanceof Event
    ) {
      message = `Event: ${error.type}`;
    }
    const wrapped = new AudioRecordingError({
      source: AudioRecordingErrorSource.AssemblyAI,
      stage,
      message,
      originalError: error instanceof Error ? error : undefined,
      details,
    });
    this.handlers.onError?.({ error: wrapped });
  }

  async start(): Promise<void> {
    this.isStarting = true;
    try {
      this.isPaused = false;
      this.shouldAttemptReconnect = true;
      this.clearReconnectTimer();
      this.isReconnecting = false;
      await this.connectForStart();
      if (!this.shouldAttemptReconnect) {
        return;
      }
      this.handlers.onStart?.();
    } catch (error) {
      if (!this.shouldAttemptReconnect) {
        return;
      }
      this.emitError(error, AudioRecordingErrorStage.Start);
      await this.stop();
      throw error;
    } finally {
      this.isStarting = false;
    }
  }

  async stop(): Promise<void> {
    this.isPaused = false;
    this.shouldAttemptReconnect = false;
    this.clearReconnectTimer();
    this.isReconnecting = false;
    try {
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        this.websocket.send(JSON.stringify({ terminate_session: true }));
      }
    } catch (error) {
      this.emitError(error, AudioRecordingErrorStage.Stop);
    } finally {
      this.teardown();
    }
  }

  dispose(): void {
    void this.stop();
  }

  private async openWebSocket(token: AssemblyAiToken): Promise<void> {
    const wsUrl = new URL(this.websocketUrl);
    const params: Record<string, string> = {
      sample_rate: String(this.sampleRateHz),
      token: token.token,
      format_turns: "true",
    };

    if (this.customVocabulary && this.customVocabulary.length > 0) {
      // AssemblyAI expects a stringified array of strings for 'keyterms_prompt'
      // https://www.assemblyai.com/docs/api-reference/streaming/realtime#query-parameters
      params.keyterms_prompt = JSON.stringify(this.customVocabulary);
    }

    Object.entries(params).forEach(([key, value]) => {
      wsUrl.searchParams.set(key, value);
    });

    await new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(wsUrl.toString());
      ws.binaryType = "arraybuffer";
      this.websocket = ws;

      const handleOpen = () => {
        ws.removeEventListener("open", handleOpen);
        ws.removeEventListener("error", handleConnectionError);
        ws.addEventListener("message", this.handleMessage);
        ws.addEventListener("close", this.handleClose);
        ws.addEventListener("error", this.handleSocketError);
        resolve();
      };

      const handleConnectionError = (event: Event) => {
        ws.removeEventListener("open", handleOpen);
        ws.removeEventListener("error", handleConnectionError);
        const networkInfo = getNetworkInfo();
        reject(
          new AudioRecordingError({
            source: AudioRecordingErrorSource.AssemblyAI,
            stage: AudioRecordingErrorStage.WebsocketConnect,
            message: "WebSocket connection failed.",
            originalError: event instanceof Error ? event : undefined,
            details: {
              readyState: ws.readyState,
              online: networkInfo.online ?? undefined,
              effectiveType: networkInfo.effectiveType,
            },
          })
        );
      };

      ws.addEventListener("open", handleOpen);
      ws.addEventListener("error", handleConnectionError);
    });
  }

  private handleMessage = (event: MessageEvent) => {
    if (typeof event.data !== "string") return;

    let rawPayload: unknown;
    try {
      rawPayload = JSON.parse(event.data);
    } catch (error) {
      this.emitError(error, AudioRecordingErrorStage.PayloadParse);
      return;
    }

    const payloadType = audioRecordingModule.resolveAssemblyAiRealtimePayloadType({
      raw: rawPayload,
    });

    this.emitStatusChange(this.transport.markConnected());

    switch (payloadType) {
      /**
       * If the payload is a turn, map it to a transcript turn and emit it.
       */
      case AssemblyAiTranscriberPayloadType.Turn: {
        const turn = audioRecordingModule.mapAssemblyAiTurnPayloadToRecorderTurn({
          raw: rawPayload,
        });
        if (!turn) return;

        if (turn.isEndOfTurn !== true) {
          this.handlers.onTranscriptPartial?.({
            partialTurn: turn as MediaRecorderTranscriptPartialTurn,
          });
          return;
        }

        this.handlers.onTranscriptFinal?.({
          finalTurn: turn as MediaRecorderTranscriptFinalTurn,
        });
        return;
      }

      /**
       * Else, no-op. We just ignore the other payload types.
       */
      default: {
        return;
      }
    }
  };

  async pause(): Promise<void> {
    if (this.isPaused) return;
    this.isPaused = true;

    try {
      if (this.audioContext && this.audioContext.state !== "closed") {
        await this.audioContext.suspend?.();
      }

      while (this.bufferedChunks.length > 0) {
        await this.flushBufferedChunks(true);
      }

      await this.processingQueue.catch(() => undefined);
      this.clearFlushTimers();
    } catch (error) {
      this.isPaused = false;
      this.emitError(error, AudioRecordingErrorStage.Pause);
      throw error;
    }
  }

  async resume(): Promise<void> {
    if (!this.isPaused) return;

    try {
      if (!this.audioContext) {
        await this.ensureAudioProcessing();
      } else if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }
      this.isPaused = false;
    } catch (error) {
      this.emitError(error, AudioRecordingErrorStage.Resume);
      throw error;
    }
  }

  private handleClose = (event: CloseEvent) => {
    logger.warn({
      message: "[WebMediaTranscriber] WebSocket closed",
      info: {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      },
    });
    this.emitStatusChange(this.transport.markDisconnected());
    this.scheduleReconnect();
  };

  private handleSocketError = (event: Event) => {
    const networkInfo = getNetworkInfo();
    this.emitError(event, AudioRecordingErrorStage.WebsocketError, {
      readyState: this.websocket?.readyState ?? undefined,
      online: networkInfo.online ?? undefined,
      effectiveType: networkInfo.effectiveType,
    });
    this.emitStatusChange(this.transport.markDisconnected());
    this.scheduleReconnect();
  };

  private async tryReconnect(): Promise<void> {
    this.clearReconnectTimer();
    let shouldScheduleRetry = false;
    this.isReconnecting = true;
    try {
      // If socket is still open, no need to reconnect.
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        return;
      }

      this.cleanupWebSocket();
      const token = await this.tokenProvider();
      await this.openWebSocket(token);
      await this.ensureAudioProcessing();
      this.emitStatusChange(this.transport.markConnected());
    } catch (error) {
      this.emitError(error, AudioRecordingErrorStage.Reconnect);
      const statusChange = this.transport.recordReconnectFailure();
      this.emitStatusChange(statusChange);
      if (this.transport.status !== TranscriptionStatus.Compromised) {
        shouldScheduleRetry = true;
      }
    } finally {
      this.isReconnecting = false;
      if (shouldScheduleRetry) {
        this.scheduleReconnect();
      }
    }
  }

  private emitStatusChange(status: TranscriptionStatus | null) {
    if (!status) return;
    this.handlers.onStatusChange?.({ status });
  }

  private scheduleReconnect() {
    if (this.isReconnecting) return;
    if (this.isStarting) return;
    if (this.transport.status === TranscriptionStatus.Compromised) return;
    if (!this.shouldAttemptReconnect) return;
    if (this.reconnectTimeoutHandle) return;

    this.isReconnecting = true;
    this.reconnectTimeoutHandle = setTimeout(() => {
      this.reconnectTimeoutHandle = null;
      void this.tryReconnect();
    }, 500);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimeoutHandle) {
      clearTimeout(this.reconnectTimeoutHandle);
      this.reconnectTimeoutHandle = null;
    }
  }

  private async connectForStart(): Promise<void> {
    await pRetry(
      async () => {
        if (!this.shouldAttemptReconnect) {
          throw new AbortError("Transcriber stopped before initial setup completed.");
        }

        const token = await this.tokenProvider();
        await this.openWebSocket(token);
        await this.ensureAudioProcessing();
        this.emitStatusChange(this.transport.markConnected());
      },
      {
        retries: this.initialConnectRetryConfig.retries,
        factor: this.initialConnectRetryConfig.factor,
        minTimeout: this.initialConnectRetryConfig.minTimeoutMs,
        maxTimeout: this.initialConnectRetryConfig.maxTimeoutMs,
        onFailedAttempt: (_error: FailedAttemptError) => {
          this.cleanupWebSocket();
          this.emitStatusChange(this.transport.markDisconnected());
        },
      }
    );
  }

  private teardown() {
    this.shouldAttemptReconnect = false;
    this.clearReconnectTimer();
    this.cleanupWebSocket();
    this.disposeAudioContext();
    this.clearBufferedChunks();
    this.handlers.onStop?.();
  }

  private cleanupWebSocket() {
    if (!this.websocket) return;
    this.websocket.removeEventListener("message", this.handleMessage);
    this.websocket.removeEventListener("close", this.handleClose);
    this.websocket.removeEventListener("error", this.handleSocketError);

    try {
      this.websocket.close();
    } catch (_) {
      /* noop */
    }

    this.websocket = null;
  }

  private disposeAudioContext() {
    if (this.audioWorkletNode) {
      this.audioWorkletNode.port.onmessage = null;

      try {
        this.audioWorkletNode.disconnect();
      } catch {
        /* noop */
      }

      this.audioWorkletNode = null;
    }

    if (this.audioSource) {
      try {
        this.audioSource.disconnect();
      } catch {
        /* noop */
      }

      this.audioSource = null;
    }

    if (this.audioContext) {
      void this.audioContext.close();
      this.audioContext = null;
    }

    if (this.workletModuleUrl) {
      URL.revokeObjectURL(this.workletModuleUrl);
      this.workletModuleUrl = null;
    }

    this.clearFlushTimers();
  }

  private async streamSamples(samples: Int16Array): Promise<void> {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      this.emitStatusChange(this.transport.markDisconnected());
      this.scheduleReconnect();
      return;
    }

    const blob = createDetachedBlobFromBufferView(samples);
    try {
      this.websocket.send(blob);
    } catch (error) {
      const networkInfo = getNetworkInfo();
      this.emitError(error, AudioRecordingErrorStage.WebsocketSend, {
        readyState: this.websocket?.readyState ?? undefined,
        online: networkInfo.online ?? undefined,
        effectiveType: networkInfo.effectiveType,
      });
      this.emitStatusChange(this.transport.markDisconnected());
      this.scheduleReconnect();
    }
  }

  private async ensureAudioProcessing(): Promise<void> {
    if (this.audioContext) {
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume().catch(error => {
          this.emitError(error, AudioRecordingErrorStage.AudioContextResume);
        });
      }
      return;
    }

    const context = new AudioContext({
      sampleRate: this.sampleRateHz,
      latencyHint: "interactive",
    });

    try {
      const processorBlob = new Blob([audioProcessorWorklet], { type: "application/javascript" });
      const moduleUrl = URL.createObjectURL(processorBlob);
      this.workletModuleUrl = moduleUrl;
      await context.audioWorklet.addModule(moduleUrl);

      const source = context.createMediaStreamSource(this.stream);
      const workletNode = new AudioWorkletNode(context, "audio-processor");
      source.connect(workletNode);
      workletNode.connect(context.destination);

      workletNode.port.onmessage = event => {
        const buffer = (event.data as { audio_data?: ArrayBuffer })?.audio_data;
        if (!buffer) return;
        this.enqueueChunk(buffer);
      };

      this.audioContext = context;
      this.audioSource = source;
      this.audioWorkletNode = workletNode;

      if (context.state === "suspended") {
        await context.resume().catch(error => {
          this.emitError(error, AudioRecordingErrorStage.AudioContextResume);
        });
      }
    } catch (error) {
      this.emitError(error, AudioRecordingErrorStage.AudioWorklet);
      await context.close().catch(() => undefined);
      this.audioContext = null;
      this.audioSource = null;
      this.audioWorkletNode = null;
      if (this.workletModuleUrl) {
        URL.revokeObjectURL(this.workletModuleUrl);
        this.workletModuleUrl = null;
      }
      throw error;
    }
  }

  private enqueueChunk(buffer: ArrayBuffer) {
    if (this.isPaused) {
      this.bufferedChunks.push(buffer);
      return;
    }

    this.bufferedChunks.push(buffer);
    this.scheduleFlushIfNeeded();
  }

  private scheduleFlushIfNeeded() {
    if (this.isPaused) {
      return;
    }

    if (this.bufferedChunks.length >= this.targetChunkCount) {
      void this.flushBufferedChunks();
    }

    if (!this.flushIntervalHandle) {
      this.flushIntervalHandle = setInterval(() => {
        void this.flushBufferedChunks();
      }, this.flushIntervalMs);
    }

    this.resetTailFlushTimeout();
  }

  private resetTailFlushTimeout() {
    if (this.tailFlushTimeoutHandle) {
      clearTimeout(this.tailFlushTimeoutHandle);
      this.tailFlushTimeoutHandle = null;
    }

    this.tailFlushTimeoutHandle = setTimeout(() => {
      void this.flushBufferedChunks(true);
    }, this.tailFlushTimeoutMs);
  }

  private async flushBufferedChunks(force = false): Promise<void> {
    if (this.bufferedChunks.length === 0) return;
    const available = this.bufferedChunks.length;
    if (!force && available < this.targetChunkCount) {
      return;
    }

    const chunkCount = Math.min(force ? available : this.targetChunkCount, this.maxChunkCount);
    const chunks = this.bufferedChunks.splice(0, chunkCount);
    const combined = this.combineChunks(chunks);

    const samples = new Int16Array(combined);
    this.processingQueue = this.processingQueue
      .then(() => this.streamSamples(samples))
      .catch(error => {
        this.emitError(error, AudioRecordingErrorStage.AudioProcessing);
      });

    if (this.bufferedChunks.length === 0 && force) {
      this.clearFlushTimers();
    }
  }

  private combineChunks(chunks: ArrayBuffer[]): ArrayBuffer {
    let totalBytes = 0;
    for (const chunk of chunks) {
      totalBytes += chunk.byteLength;
    }

    const combined = new Uint8Array(totalBytes);
    let offset = 0;
    for (const chunk of chunks) {
      combined.set(new Uint8Array(chunk), offset);
      offset += chunk.byteLength;
    }

    return combined.buffer;
  }

  private clearFlushTimers() {
    if (this.flushIntervalHandle) {
      clearInterval(this.flushIntervalHandle);
      this.flushIntervalHandle = null;
    }
    if (this.tailFlushTimeoutHandle) {
      clearTimeout(this.tailFlushTimeoutHandle);
      this.tailFlushTimeoutHandle = null;
    }
  }

  private clearBufferedChunks() {
    this.bufferedChunks = [];
    this.clearFlushTimers();
  }
}
